import { useEffect, useState } from 'react'
import { useRealtimeStore } from '../../../shared/realtime/realtimeStore'
import { DateRangePopover } from '../../../shared/ui/DateRangePopover'
import type { DashboardPeriodKey } from './types'

const PERIODS: { key: DashboardPeriodKey; label: string }[] = [
  { key: 'day', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'quarter', label: 'Квартал' },
  { key: 'year', label: 'Год' },
]

const RU_MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
]

/** «2 янв – 24 мая» из двух YYYY-MM-DD. */
function rangeLabel(from: string | null, to: string | null): string {
  if (!from) return 'Период…'
  const fmt = (iso: string) => {
    const [, m, d] = iso.split('-').map(Number)
    return `${d} ${RU_MONTHS_SHORT[(m ?? 1) - 1]}`
  }
  return `${fmt(from)} – ${fmt(to ?? from)}`
}

function formatAgo(seconds: number): string {
  if (seconds < 60) return `${seconds} сек назад`
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`
  return `${Math.floor(seconds / 3600)} ч назад`
}

export interface DashboardHeaderProps {
  period: DashboardPeriodKey
  onPeriodChange: (key: DashboardPeriodKey) => void
  /** Текущий custom-диапазон в YYYY-MM-DD (для подписи кнопки и поповера). */
  customFrom?: string | null
  customTo?: string | null
  onCustomChange?: (from: string, to: string) => void
  fetchedAt: number | null
  onExport?: () => void
}

export function DashboardHeader({
  period,
  onPeriodChange,
  customFrom = null,
  customTo = null,
  onCustomChange,
  fetchedAt,
  onExport,
}: DashboardHeaderProps) {
  const wsStatus = useRealtimeStore((s) => s.status)
  const [now, setNow] = useState(() => Date.now())
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const ago = fetchedAt ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : null
  const live = wsStatus === 'connected'

  return (
    <div className="flex items-start justify-between flex-wrap gap-3">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Панель управления</h1>
        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
          {ago !== null && <span>Обновлено {formatAgo(ago)}</span>}
          <span>·</span>
          <span
            className={`inline-flex items-center gap-1 ${
              live ? 'text-emerald-600' : 'text-gray-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300'}`}
            />
            {live ? 'Live' : 'Offline'}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* relative-обёртка: поповер рендерится СНАРУЖИ overflow-hidden группы,
            иначе календарь обрезается рамкой сегментированного контрола. */}
        <div className="relative">
          <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => onPeriodChange(p.key)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  period === p.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
            {onCustomChange && (
              <button
                onClick={() => setPickerOpen((v) => !v)}
                className={`px-3 py-1.5 text-sm transition-colors border-l border-gray-200 tabular-nums ${
                  period === 'custom' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {period === 'custom' && customFrom ? rangeLabel(customFrom, customTo) : 'Период…'}
              </button>
            )}
          </div>
          {pickerOpen && onCustomChange && (
            <DateRangePopover
              from={customFrom}
              to={customTo}
              onApply={onCustomChange}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
        {onExport && (
          <button
            onClick={onExport}
            className="px-3 py-1.5 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          >
            Экспорт
          </button>
        )}
      </div>
    </div>
  )
}
